import React, { useMemo, useState, useEffect } from "react";
import {
  ArrowLeft,
  CalendarDays,
  X,
  ChevronDown,
  Plus,
  Check,
} from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

const formatDisplayDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateForInput = (date) => {
  return date.toISOString().split("T")[0];
};

export default function FillVendor({ rfq, onClose }) {
  const { addAlert } = useAlert();
  const [organisationId, setOrganisationId] = useState(
    JSON.parse(localStorage.getItem("organization"))?.id
  ); // Replace with actual org ID
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [isNewVendor, setIsNewVendor] = useState(false);

  // New vendor details
  const [vendorName, setVendorName] = useState("");
  const [vendorAddress, setVendorAddress] = useState("");
  const [vendorPhone, setVendorPhone] = useState("");

  // Delivery date
  const [deliveryDate, setDeliveryDate] = useState(
    formatDateForInput(new Date())
  );
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Materials with pricing
  const [materials, setMaterials] = useState([]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // GST options
  const gstOptions = [5, 8, 12, 18, 28];

  useEffect(() => {
    fetchVendors();
    initializeMaterials();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BASE_URL}/inventory/list-org-vendors/${organisationId}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`, // Replace with actual token retrieval
          },
        }
      );
      const result = await response.json();
      if (result.status) {
        setVendors(result.data);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    } finally {
      setLoading(false);
    }
  };

  const initializeMaterials = () => {
    const initialMaterials =
      rfq?.lines?.map((line, idx) => ({
        id: idx,
        rfq_line_id: line.id,
        material_id: line.material,
        material_name: line.material_name,
        quantity: line.quantity,
        uom: line.uom,
        unit_price: line.price || "",
        gst_percent: 0,
        tax_amount: 0,
        total_price: line.price || 0,
      })) || [];
    setMaterials(initialMaterials);
  };

  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor);
    setIsNewVendor(false);
    setVendorName(vendor.name);
    setVendorPhone(vendor.contact_no);
    setVendorAddress("");
    setShowVendorDropdown(false);
  };

  const handleNewVendor = () => {
    setSelectedVendor(null);
    setIsNewVendor(true);
    setVendorName("");
    setVendorPhone("");
    setVendorAddress("");
    setShowVendorDropdown(false);
  };

  const handleUnitPriceChange = (materialId, value) => {
    setMaterials((prev) =>
      prev.map((m) => {
        if (m.id === materialId) {
          const unitPrice = parseFloat(value) || 0;
          const taxAmount = (unitPrice * m.quantity * m.gst_percent) / 100;
          const totalPrice = unitPrice * m.quantity + taxAmount;
          return {
            ...m,
            unit_price: unitPrice,
            tax_amount: taxAmount,
            total_price: totalPrice,
          };
        }
        return m;
      })
    );
  };

  const handleGstChange = (materialId, gstPercent) => {
    setMaterials((prev) =>
      prev.map((m) => {
        if (m.id === materialId) {
          const taxAmount = (m.unit_price * m.quantity * gstPercent) / 100;
          const totalPrice = m.unit_price * m.quantity + taxAmount;
          return {
            ...m,
            gst_percent: gstPercent,
            tax_amount: taxAmount,
            total_price: totalPrice,
          };
        }
        return m;
      })
    );
  };

  const grandTotal = useMemo(() => {
    return materials.reduce((sum, m) => sum + m.total_price, 0);
  }, [materials]);

  const handleSubmit = async () => {
    // Validation
    if (!isNewVendor && !selectedVendor) {
      alert("Please select a vendor");
      return;
    }
    if (isNewVendor && (!vendorName || !vendorPhone || !vendorAddress)) {
      alert("Please fill all vendor details");
      return;
    }

    const payload = {
      request_for_quotation: rfq.id,
      ...(selectedVendor && { vendor: selectedVendor.id }),
      vendor_name: vendorName,
      address: vendorAddress,
      phone: vendorPhone,
      total_price: grandTotal,
      delivery_date: deliveryDate,
      lines: materials.map((m) => ({
        rfq_lines: m.rfq_line_id,
        material: m.material_id,
        quantity: m.quantity,
        unit_price: m.unit_price,
        tax_amount: m.tax_amount,
        total_price: m.total_price,
      })),
    };

    try {
      setSubmitting(true);
      const token = "your-bearer-token"; // Get from localStorage or context
      const response = await fetch(
        `${BASE_URL}/inventory/applications/create/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`, // Replace with actual token retrieval
          },
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json();
      if (result.status) {
        addAlert("Application created successfully!", "success");
        onClose();
      } else {
        addAlert("Failed to create application", "error");
      }
    } catch (error) {
      console.error("Error creating application:", error);
      addAlert("Error creating application", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed right-0 top-16 w-1/2 h-[calc(100vh-4rem)] bg-white border-l border-gray-200 shadow-xl z-50 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-gray-200 flex items-center justify-center text-[#232323]"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="text-base font-semibold text-[#232323]">
              Fill for Vendor
            </div>
            <div className="text-xs text-[#232323]">
              {rfq?.rfq_number} | {rfq?.material_request_number}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        <div className="mb-4">
          <span className="text-xs text-[#232323] mr-1">Quotation ID</span>
          <span className="inline-flex items-center px-3 py-1 bg-gray-200 rounded-full text-sm font-medium text-[#232323]">
            QT_VN_005
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 mb-4">
          {/* Vendor Selection */}
          <div>
            <div className="text-sm font-medium text-[#232323] mb-1">
              Vendor
            </div>
            <div className="relative">
              <button
                onClick={() => setShowVendorDropdown(!showVendorDropdown)}
                className="w-full h-10 px-3 flex items-center justify-between bg-gray-200 rounded-md text-sm text-[#232323] hover:bg-gray-300"
              >
                <span>
                  {selectedVendor
                    ? selectedVendor.name
                    : isNewVendor
                    ? "New Vendor"
                    : "Select Vendor"}
                </span>
                <ChevronDown size={18} />
              </button>

              {showVendorDropdown && (
                <div className="absolute top-full mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
                  <button
                    onClick={handleNewVendor}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 border-b"
                  >
                    <Plus size={16} />
                    <span className="font-medium">Add New Vendor</span>
                  </button>
                  {vendors.map((vendor) => (
                    <button
                      key={vendor.id}
                      onClick={() => handleVendorSelect(vendor)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center justify-between"
                    >
                      <div>
                        <div className="font-medium">{vendor.name}</div>
                        <div className="text-xs text-gray-500">
                          {vendor.contact_no}
                        </div>
                      </div>
                      {selectedVendor?.id === vendor.id && (
                        <Check size={16} className="text-green-600" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* New Vendor Details */}
          {isNewVendor && (
            <>
              <div>
                <div className="text-sm font-medium text-[#232323] mb-1">
                  Vendor Name
                </div>
                <input
                  type="text"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  className="w-full h-10 px-3 bg-gray-100 border border-gray-200 rounded-md text-sm text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter vendor name"
                />
              </div>
              <div>
                <div className="text-sm font-medium text-[#232323] mb-1">
                  Phone Number
                </div>
                <input
                  type="text"
                  value={vendorPhone}
                  onChange={(e) => setVendorPhone(e.target.value)}
                  className="w-full h-10 px-3 bg-gray-100 border border-gray-200 rounded-md text-sm text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <div className="text-sm font-medium text-[#232323] mb-1">
                  Address
                </div>
                <textarea
                  value={vendorAddress}
                  onChange={(e) => setVendorAddress(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-sm text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter address"
                  rows={3}
                />
              </div>
            </>
          )}

          {/* Delivery Date */}
          <div>
            <div className="text-sm font-medium text-[#232323] mb-1">
              Select Delivery Date
            </div>
            <div className="relative">
              <input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                className="w-full h-10 px-3 bg-gray-200 rounded-md text-sm text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>

        {/* Materials */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-base font-medium text-gray-900">Materials</div>
            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">
              {materials.length}
            </span>
          </div>

          <div className="space-y-3">
            {materials.map((m) => (
              <div key={m.id} className="border border-gray-200 rounded-lg p-3">
                <div className="h-10 px-3 bg-gray-200 rounded-md flex items-center justify-between text-sm text-gray-700 mb-2">
                  <span>{m.material_name}</span>
                </div>

                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-1 h-10 px-3 bg-gray-200 rounded-md flex items-center text-sm text-gray-700">
                    {m.quantity}
                  </div>
                  <div className="h-10 px-3 bg-gray-200 rounded-md flex items-center text-xs text-gray-500">
                    {m.uom}
                  </div>
                  <span className="text-xs text-[#232323]">
                    <X size={18} />
                  </span>
                  <div className="h-10 px-3 bg-gray-200 rounded-md flex items-center text-xs text-gray-500">
                    ₹
                  </div>
                  <input
                    type="number"
                    value={m.unit_price}
                    onChange={(e) =>
                      handleUnitPriceChange(m.id, e.target.value)
                    }
                    className="flex-1 h-10 px-3 bg-gray-100 border border-gray-200 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Unit price"
                  />
                </div>

                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {gstOptions.map((gst) => (
                    <button
                      key={gst}
                      onClick={() => handleGstChange(m.id, gst)}
                      className={`px-3 py-1.5 text-xs border rounded-full ${
                        m.gst_percent === gst
                          ? "bg-[#3B0069] text-white border-[#3B0069]"
                          : "bg-[#ECE6F0] border-gray-200 text-[#3B0069] hover:bg-gray-100"
                      }`}
                    >
                      GST {gst}%
                    </button>
                  ))}
                </div>

                {m.gst_percent > 0 && (
                  <div className="mt-2 text-xs text-gray-600">
                    Tax Amount: ₹ {m.tax_amount.toFixed(2)}
                  </div>
                )}

                <div className="mt-2 flex items-center justify-between text-sm">
                  <div className="text-gray-600">Total Price</div>
                  <div className="font-semibold text-gray-900">
                    ₹ {m.total_price.toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between text-sm border-t pt-4">
          <div className="text-gray-600 font-medium">Grand Total</div>
          <div className="font-bold text-gray-900 text-lg">
            ₹ {grandTotal.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="border-t border-gray-200 px-4 py-3 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 h-10 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="flex-1 h-10 bg-[#3B0069] text-white rounded-md text-sm font-medium hover:bg-[#2d0050] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Submitting..." : "Submit Application"}
        </button>
      </div>
    </div>
  );
}
